(() => {
  const SECTION_SELECTOR = '.oddit-section-retreats';

  function initSection(section) {
    const tabs = section.querySelectorAll('.oddit-section-retreats__tab');
    const cardsWrapper = section.querySelector('.oddit-section-retreats__cards-wrapper');
    const cards = section.querySelectorAll('.oddit-section-retreats__card');
    const dotsContainer = section.querySelector('[data-dots]');
    const prevBtn = section.querySelector('[data-scroll-prev]');
    const nextBtn = section.querySelector('[data-scroll-next]');
    const isCarousel = section.dataset.layout !== 'grid';

    function getVisibleCards() {
      return Array.from(cards).filter((c) => c.style.display !== 'none');
    }

    function getCardStep() {
      const visibleCards = getVisibleCards();
      if (!visibleCards.length) return 0;
      // Card-to-card distance: difference between two cards' offsetLeft if 2+ exist;
      // otherwise fall back to the card's own width (no gap layout = card width).
      if (visibleCards.length >= 2) {
        return visibleCards[1].offsetLeft - visibleCards[0].offsetLeft;
      }
      return visibleCards[0].offsetWidth;
    }

    function getMaxScroll() {
      if (!cardsWrapper) return 0;
      return Math.max(0, cardsWrapper.scrollWidth - cardsWrapper.clientWidth);
    }

    function getCurrentCardIndex() {
      const step = getCardStep();
      if (!step || !cardsWrapper) return 0;
      return Math.round(cardsWrapper.scrollLeft / step);
    }

    // ---- Scroll a whole page (= integer cards that fit in the visible wrapper) ----
    function getPageStep() {
      if (!cardsWrapper) return 0;
      const cardStep = getCardStep();
      if (!cardStep) return cardsWrapper.clientWidth;
      const cardsPerPage = Math.max(1, Math.floor(cardsWrapper.clientWidth / cardStep));
      return cardsPerPage * cardStep;
    }

    function scrollByCards(direction) {
      if (!cardsWrapper) return;
      const step = getPageStep();
      if (!step) return;
      // Snap to nearest card boundary so the LEFT edge always lands on a clean card start
      // (no cropped sliver of the previous card peeking in after scroll).
      const cardStep = getCardStep();
      const currentCardIndex = getCurrentCardIndex();
      const cardsPerPage = Math.max(1, Math.round(step / cardStep));
      const targetCardIndex = currentCardIndex + (direction * cardsPerPage);
      const visibleCount = getVisibleCards().length;
      const maxIndex = Math.max(0, visibleCount - cardsPerPage);
      const clampedIndex = Math.min(Math.max(targetCardIndex, 0), maxIndex);
      cardsWrapper.scrollTo({ left: clampedIndex * cardStep, behavior: 'smooth' });
    }

    function updateArrowVisibility() {
      if (!cardsWrapper) return;
      const max = getMaxScroll();
      const overflowing = max > 4;
      const atStart = cardsWrapper.scrollLeft <= 4;
      const atEnd = cardsWrapper.scrollLeft >= max - 4;
      if (prevBtn) prevBtn.hidden = !overflowing || atStart;
      if (nextBtn) nextBtn.hidden = !overflowing || atEnd;
    }

    function rebuildDots() {
      if (!dotsContainer) return;
      const visibleCount = getVisibleCards().length;
      const cardStep = getCardStep();
      const cardsPerPage = cardStep && cardsWrapper
        ? Math.max(1, Math.floor(cardsWrapper.clientWidth / cardStep))
        : 1;
      const pageCount = Math.max(1, Math.ceil(visibleCount / cardsPerPage));

      let html = '';
      for (let i = 0; i < pageCount; i++) {
        const activeClass = i === 0 ? ' oddit-section-retreats__dot--active' : '';
        html += '<button type="button" class="oddit-section-retreats__dot' + activeClass + '" data-dot-index="' + i + '" aria-label="Go to page ' + (i + 1) + '"></button>';
      }
      dotsContainer.innerHTML = html;

      const dots = dotsContainer.querySelectorAll('.oddit-section-retreats__dot');
      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.getAttribute('data-dot-index'), 10);
          if (isNaN(idx) || !cardStep || !cardsWrapper) return;
          cardsWrapper.scrollTo({ left: idx * cardsPerPage * cardStep, behavior: 'smooth' });
        });
      });
    }

    function updateActiveDot() {
      if (!dotsContainer || !cardsWrapper) return;
      const dots = dotsContainer.querySelectorAll('.oddit-section-retreats__dot');
      if (!dots.length) return;
      const cardStep = getCardStep();
      if (!cardStep) return;
      const cardsPerPage = Math.max(1, Math.floor(cardsWrapper.clientWidth / cardStep));
      const currentPage = Math.round(cardsWrapper.scrollLeft / (cardsPerPage * cardStep));
      const clampedPage = Math.min(Math.max(currentPage, 0), dots.length - 1);
      dots.forEach((dot, i) => {
        dot.classList.toggle('oddit-section-retreats__dot--active', i === clampedPage);
      });
    }

    // ---- Init ----
    const activeTab = section.querySelector('.oddit-section-retreats__tab--active');
    if (activeTab) {
      filterCards(cards, activeTab.dataset.tabValue);
    }
    if (isCarousel) {
      rebuildDots();
      updateArrowVisibility();
    }

    // ---- Tab switching ----
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const value = tab.dataset.tabValue;

        tabs.forEach((t) => {
          t.classList.remove('oddit-section-retreats__tab--active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('oddit-section-retreats__tab--active');
        tab.setAttribute('aria-selected', 'true');

        if (cardsWrapper) {
          cardsWrapper.setAttribute('aria-labelledby', tab.id);
        }

        filterCards(cards, value);

        if (isCarousel && cardsWrapper) {
          cardsWrapper.scrollLeft = 0;
          rebuildDots();
          updateArrowVisibility();
        }
      });
    });

    // ---- Free-scroll keeps dots/arrows in sync ----
    if (isCarousel && cardsWrapper) {
      let scrollDebounce;
      cardsWrapper.addEventListener('scroll', () => {
        clearTimeout(scrollDebounce);
        scrollDebounce = setTimeout(() => {
          updateArrowVisibility();
          updateActiveDot();
        }, 80);
      }, { passive: true });
    }

    // ---- Arrows ----
    if (prevBtn) prevBtn.addEventListener('click', () => scrollByCards(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => scrollByCards(1));

    // ---- Resize ----
    let resizeDebounce;
    window.addEventListener('resize', () => {
      clearTimeout(resizeDebounce);
      resizeDebounce = setTimeout(() => {
        if (!isCarousel) return;
        rebuildDots();
        updateArrowVisibility();
        updateActiveDot();
      }, 120);
    });
  }

  function filterCards(cards, tabValue) {
    const showAll = !tabValue || tabValue === 'all';
    cards.forEach((card) => {
      const group = card.dataset.tabGroup;
      if (showAll || group === tabValue) {
        card.style.display = '';
      } else {
        card.style.display = 'none';
      }
    });
  }

  function destroySection() {
    // Listeners are attached to DOM elements that are GC'd when the section unloads
  }

  document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection();
  });
})();
